import { Component, Input, Output, EventEmitter, OnInit, OnChanges, Inject } from 'ng-metadata/core';
import { INavItem, AppContainerStateChangeName, IAppContainerCompanyState, IAppContainerState, IAppContainerPlaceholderNavItem } from '../shared/shared.interfaces';
import * as angular from 'angular';
import './navigation.component.scss';
import { AppContainerStateService } from '../app-container-state.service';

const APP_CONTAINER_STATE_COOKIE_KEY = 'mwAppContainerState';

/**
 * @ngdoc directive
 * @name mwNavigation
 * @module mw.components.appContainer
 *
 * @param {array=} items An array of navigation item objects.  Each item has the following structure:
 * <hljs hljs-language="js">
 *   {
 *      icon: 'meltwater:inbox_24px',
 *      isActive: true,
 *      link: '/inbox',
 *      title: 'Inbox'
 *   }
 * </hljs>
 *
 * @param {array} [items] - List of navigation items that should be displayed.
 * @param {expression} [on-select] - Angular expression to evaluate when a navigation item is selected.  $event is the selected navigation item object.
 * @param {expression} [on-maximize] - Angular expression to evaluate when the navigation menu is changed to a maximized state.
 * @param {expression} [on-minimize] - Angular expression to evaluate when the navigation menu is changed to a minimized state.
 * @param {expression} [on-header-click] - Angular expression to evaluate when the main header logo is clicked.  $event is the ng-click event.
 *
 * @description
 * `<md-navigation>` is a component used to display a responsive application navigation panel.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-navigation items="$ctrl.navItems" on-select="$ctrl.onSelect($event)"></mw-navigation>
 * </hljs>
 *
 */
@Component({
    selector: 'mw-navigation',
    template: `
        <md-sidenav md-component-id="mwNavigationSideNav" class="md-sidenav-left md-animate md-whiteframe-z1 mw-navigation" ng-class="['mw-navigation--'+$ctrl.getScreenSize(), {'mw-navigation--minimized': $ctrl.isMinimized}]" layout="column" md-is-locked-open="$ctrl.isLockedOpen()" >
            <header class="mw-navigation__header md-hue-3" layout="row" layout-align="start start" ng-click="$ctrl.headerClicked($event)">
                <md-icon class="mw-navigation__eyecon-glyph md-primary" md-svg-src="{{ $ctrl.eyeconGlyphSvgPath }}"></md-icon>
                <md-icon class="mw-navigation__eyecon-text md-primary" md-svg-src="{{ $ctrl.eyeconTextSvgPath }}"></md-icon>
            </header>
            <md-content id="mwNavigationContent" class="mw-navigation__content" flex="flex" ng-click="$ctrl.toggleNavSize($event)" ng-mouseover="$ctrl.onContentMouseOver($event)" ng-mouseleave="$ctrl.onContentMouseLeave($event)">
                <md-subheader class="mw-navigation__scroll-separator"><!-- empty subheader used for sticky separator styling when the md-list scrolls--></md-subheader>
                <md-list class="mw-navigation__list">
                    <md-list-item ng-repeat="item in $ctrl.items track by $id(item)" role="link">
                        <md-tooltip class="mw-navigation__list-item-tooltip" md-visible="false" md-direction="right" ng-if="$ctrl.isMinimized && $ctrl.isLockedOpen()" md-delay="200"><span translate="{{ $ctrl.getItemTooltip(item) }}" translate-default="{{ $ctrl.getItemTooltip(item) }}"></span></md-tooltip>
                        <md-button ng-attr-target="{{ $ctrl.getLinkTarget(item) }}" class="mw-navigation__list-item md-no-style" ng-class="{'mw-navigation__list-item--active': item.isActive, 'expanded': !$ctrl.isMinimized, 'mw-navigation__list-item--icon-right': item.openInNewTab}" ng-attr-href="{{item.link !== undefined && item.link.length > 0 && item.link || undefined}}" ng-click="$ctrl.selectItem(item, $event)" md-no-ink>
                            <md-icon class="mw-navigation__list-item-icon" md-svg-icon="{{item.icon}}" aria-label="{{item.title | translate}}">
                            </md-icon>
                            <span class="mw-navigation__list-item-text" translate="{{item.title}}">{{item.title}}</span>
                            <md-icon ng-if="item.openInNewTab" class="mw-navigation__list-item-icon mw-navigation__list-item-icon--right" md-svg-icon="action:ic_open_in_new_24px"></md-icon>
                        </md-button>
                    </md-list-item>
                </md-list>
            </md-content>
            <footer class="mw-navigation__footer" layout="row">
                <div class="mw-navigation__company" layout="column" flex="auto">
                    <div class="mw-navigation__company-info">
                        <div>Meltwater &copy;{{$ctrl.copywrite}}</div>
                    </div>
                    <div class="mw-navigation__company-info-links" layout="row">
                        <span ng-repeat="footerLink in $ctrl.footerLinks">
                            <a ng-href="{{ footerLink.href }}" target="_blank" translate="{{footerLink.bemKey}}" translate-default="{{ footerLink.defaultTranslation }}"></a>
                            <span ng-if="!$last">&nbsp;&#8226;&nbsp;</span>
                        </span>
                    </div>
                </div>
                <md-button ng-if="!$ctrl.isMinimized  || !$ctrl.isLockedOpen()" id="mwNavigationCollapseButton" class="mw-navigation__toggle-button md-icon-button" md-no-ink ng-click="$ctrl.toggleNavSize($event)" aria-label="{{$ctrl.collapseText | translate}}">
                    <md-icon id="mwNavigationExpandButtonIcon" md-svg-icon="hardware:ic_keyboard_arrow_left_24px" class="s36"></md-icon>
                    <md-tooltip class="mw-navigation__toggle-tooltip" md-visible="$ctrl.showToggleTooltip" md-direction="right" md-delay="$ctrl.toggleTooltipDelay"><span translate>{{$ctrl.collapseText}}</span></md-tooltip>
                </md-button>
                <md-button ng-if="$ctrl.isMinimized && $ctrl.isLockedOpen()" id="mwNavigationExpandButton" class="mw-navigation__toggle-button md-icon-button" md-no-ink ng-click="$ctrl.toggleNavSize($event)" aria-label="{{$ctrl.expandText | translate}}">
                    <md-icon id="mwNavigationCollapseButtonIcon" md-svg-icon="hardware:ic_keyboard_arrow_right_24px" class="s36"></md-icon>
                    <md-tooltip class="mw-navigation__toggle-tooltip" md-visible="$ctrl.showToggleTooltip" md-direction="right" md-delay="$ctrl.toggleTooltipDelay"><span translate>{{$ctrl.expandText}}</span></md-tooltip>
                </md-button>
            </footer>
        </md-sidenav>
    `
})
export class NavigationComponent implements OnInit, OnChanges {
    @Input('<') items: INavItem[] = [];
    @Output() onSelect = new EventEmitter<INavItem>();
    @Output() onMinimize = new EventEmitter<any>();
    @Output() onMaximize = new EventEmitter<any>();
    @Output() onHeaderClick = new EventEmitter<ng.IAngularEvent>();

    private collapseText: string = 'navigation-panel--collapse';
    private expandText: string = 'navigation-panel--expand';
    private copywrite: number = (new Date()).getFullYear();
    private selectedItem: INavItem = null;
    private showToggleTooltip: boolean = false;
    private showToggleTooltipTimeout: any;
    private sideNavId: string = 'mwNavigationSideNav';
    private targetPattern: RegExp = /^https?:\/\//;
    private toggleTooltipDelay: number = 500;
    private isMinimized: boolean = true;
    private isLockedOpenState: boolean;
    private footerLinks = [
        {
            bemKey: 'footer-link--copyright',
            defaultTranslation: 'Copyright',
            href: 'http://www.meltwater.com/copyright/'
        },
        {
            bemKey: 'footer-link--privacy',
            defaultTranslation: 'Privacy',
            href: 'http://www.meltwater.com/privacy-policy/'
        },
        {
            bemKey: 'footer-link--contact',
            defaultTranslation: 'Contact',
            href: 'http://support.meltwater.com/hc/'
        }
    ];

    private eyeconGlyphSvgPath: String = `${process.env.assetsPath}icons/meltwater-icons/eyecon-glyph.svg`;
    private eyeconTextSvgPath: String = `${process.env.assetsPath}icons/meltwater-icons/eyecon-text.svg`

    constructor(@Inject('$mdMedia') private $mdMedia: ng.material.IMedia,
                @Inject('$mdSidenav') private $mdSidenav: ng.material.ISidenavService,
                @Inject('$cookies') private $cookies: ng.cookies.ICookiesService,
                @Inject('$timeout') private $timeout: ng.ITimeoutService,
                @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
                @Inject('mwAppContainerStateService') private mwAppContainerStateService: AppContainerStateService,
                @Inject('$translate') private $translate: ng.translate.ITranslateService) {
    }

    ngOnInit() {
        this.isMinimized = this.mwAppContainerStateService.getIsNavigationMinimized();
        this.$rootScope.$on('$translateChangeSuccess', () => {
            this.updateNavState();
            this.updateFooterState();
        });
    }

    ngOnChanges(changes) {
        if (this.items && changes.items && changes.items.currentValue.length) {
            this.updateNavState();
        }
    }

    getScreenSize() {
        if (this.$mdMedia('xs')) { return 'xs'; }
        if (this.$mdMedia('sm')) { return 'sm'; }
        if (this.$mdMedia('md')) { return 'md'; }
        if (this.$mdMedia('lg')) { return 'lg'; }
        if (this.$mdMedia('xl')) { return 'xl'; }
    }

    isLockedOpen() {
        let isLockedOpen = this.$mdMedia('gt-sm');
        if (isLockedOpen) {
            // Before locking the sidenav open, we need to first ensure it is in a 'closed' state.  Yes, this is confusing!
            // But unless we do this, we run into a few undesired UX/UI problems when transitioning between xs/sm and md/lg/xl screen sizes.
            // One such problem is described in https://jira.meltwater.com/browse/MAAS-1089

            this.$mdSidenav(this.sideNavId).close();
        }

        if (this.isLockedOpenState !== isLockedOpen) {
            this.isLockedOpenState = isLockedOpen;
            this.emitSizeToggledEvent();
        }

        return isLockedOpen;
    }

    toggleNavSize(event) {
        if (event.target && event.target.id && (event.target.id === 'mwNavigationContent' || event.target.id === 'mwNavigationExpandButton'
            || event.target.id === 'mwNavigationCollapseButton' || event.target.id === 'mwNavigationExpandButtonIcon' || event.target.id === 'mwNavigationCollapseButtonIcon')) {
            if (this.$mdMedia('gt-sm')) {
                if (this.isMinimized) {
                    this.maximize();
                } else {
                    this.minimize();
                }
            } else {
                this.$mdSidenav(this.sideNavId).close();
            }

           this.emitSizeToggledEvent();
        }
    }

    onContentMouseOver($event) {
        if ($event && $event.target && $event.target.id === 'mwNavigationContent') {
            this.showToggleTooltipTimeout = this.$timeout(() => {
                this.showToggleTooltipTimeout = null;
                this.showToggleTooltip = true;
            }, this.toggleTooltipDelay);
        } else {
            this.hideToggleTooltip();
        }
    }

    onContentMouseLeave($event) {
        this.hideToggleTooltip();
    }

    hideToggleTooltip() {
        if (this.showToggleTooltipTimeout) {
            this.$timeout.cancel(this.showToggleTooltipTimeout);
            this.showToggleTooltipTimeout = null;
        }

        this.showToggleTooltip = false;
    }

    maximize() {
        if (this.isMinimized) {
            this.isMinimized = false;
            this.hideToggleTooltip();

            this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.isNavigationMinimized, false);

            this.onMaximize.emit({});
        }
    }

    minimize() {
        if (!this.isMinimized) {
            this.isMinimized = true;
            this.hideToggleTooltip();

            this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.isNavigationMinimized, true);

            this.onMinimize.emit({});
        }
    }

    selectItem(item, $event) {
        // Do nothing if this link opens in a new tab
        if (item.openInNewTab) return false;

        // Reset all to inactive and and then set new one be active
        for (let key in this.items) {
            this.items[key].isActive = false;
        }

        this.selectedItem = item;
        this.selectedItem.isActive = true;

        if (this.$mdMedia('xs') || this.$mdMedia('sm')) {
            this.$mdSidenav(this.sideNavId).close();
        }

        this.updateNavState();

        this.onSelect.emit(this.selectedItem);

        return false;
    }

    headerClicked($event) {
        this.onHeaderClick.emit($event);
    }

    getLinkTarget(item) {
        if (item.openInNewTab) {
            return '_blank';
        }

        return this.targetPattern.test(item.link) ? '_self': '';
    }

    getItemTooltip(item) {
        return item.openInNewTab ? item.title + ' (in a new window)' : item.title;
    }

    emitSizeToggledEvent() {
        if (this.$rootScope) {
            let count = 1;
            let moveStickyHeader = setInterval(() => {
                this.$rootScope.$emit('mw-navigation:size-toggled');
                count++;

                if (count === 250) {
                    clearInterval(moveStickyHeader);
                    this.$rootScope.$emit('mw-navigation:size-toggle-complete');
                }
            }, 1)
        }
    }

    updateNavState(): void {
        let navItems = angular.copy(this.items);
        let navItemsState: Array<IAppContainerPlaceholderNavItem> = [];

        // Do not override the existing nav items if navItems length is 0
        // to avoid white flicker of nav items
        if (!navItems.length) {
            return;
        }

        for (let i = 0; i < navItems.length; i++) {
            let newNavItem: IAppContainerPlaceholderNavItem;

            newNavItem = {
                title: this.$translate.instant(navItems[i].title),
                icon: navItems[i].icon,
                isActive: navItems[i].isActive
            };

            navItemsState.push(newNavItem);
        }

        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.navigation, navItemsState);
    }

    updateFooterState(): void {
        let translatedFooterLinks: Array<string> = [];

        for (let i = 0; i < this.footerLinks.length; i++) {
            let translation = this.$translate.instant(this.footerLinks[i].bemKey).indexOf('--') > -1 ? this.footerLinks[i].defaultTranslation : this.$translate.instant(this.footerLinks[i].bemKey);

            translatedFooterLinks.push(translation);
        }

        this.mwAppContainerStateService.updateAppContainerState(AppContainerStateChangeName.footerLinks, translatedFooterLinks);
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/app-container/navigation/navigation.component.ts